import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import {
    Form,
    Input,
    Button,
    Select,
    Radio,
    Checkbox,
    Switch,
    DatePicker,
    TimePicker,
    Upload,
    Row,
    Col,
} from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import './style.css'
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';


dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);


const DynamicForm = (props) => {
    const { form, onSubmit } = props
    const current = dayjs();

    const validationSchema = Yup.object().shape(form?.formValidation);

    const { watch, control, reset, handleSubmit, setValue, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(validationSchema),
        mode: 'onChange'
    });
    // const [passwordVisible, setPasswordVisible] = useState(false);
    // const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
    const [customComponentData, setCustomComponentData] = useState(null)
    const [selectedRadioValue, setSelectedRadioValue] = useState(0);


    // Watch all fields
    const allFields = watch();

    const formResponse = (data) => {
        onSubmit(data)
    };

    const handleReset = () => {
        reset();
    };

    const handleCustomComponentStateChange = (field) => {
        const currentState = getValues();
        setCustomComponentData({ field, value: currentState[field] })
    };

    const disablePreviousTimes = (currentTime) => {
        if (!currentTime) {
            return false;
        }
        // Check if the selected date is today
        const isToday = currentTime.isSame(current, 'day');

        // Disable hours, minutes, and seconds if the date is today
        return isToday && (
            currentTime.hour() < current.hour() ||
            (currentTime.hour() === current.hour() && currentTime.minute() < current.minute()) ||
            (currentTime.hour() === current.hour() && currentTime.minute() === current.minute() && currentTime.second() < current.second())
        );
    };

    // Create separate state for each field based on its name
    const [visibility, setVisibility] = useState({
        password: false,
        confirmPassword: false,
        changePassword: false // Add as many as needed
    });

    const handleVisibilityChange = (name) => {
        setVisibility((prevState) => ({
            ...prevState,
            [name]: !prevState[name], // Toggle the visibility of the specific field
        }));
    };

    const handleSelectedRadioValue = (fieldName, value) => {
        setSelectedRadioValue(value)
        setValue(fieldName, value)
    }

    const renderFormField = (field) => {
        if (!field) return null;

        switch (field.type) {
            case 'text':
            case 'number':
            case 'email':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue=""
                        render={(param) => (
                            <Input
                                {...param.field}
                                className={field.classes}
                                placeholder={field.placeholder}
                                type={field.type}
                                suffix={field.icon}
                                readOnly={field.readOnly || false}
                                disabled={field?.disabled ?? false}
                            />
                        )}
                    />
                )
            case 'password':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue=""
                        render={(param) => (
                            <Input.Password
                                {...param.field}
                                className={field.classes}
                                placeholder={field.placeholder}
                                type={field.type}
                                suffix={field.icon}
                                iconRender={(visible) => (visible ? <FaEye /> : <FaEyeSlash />)}
                                visibilityToggle={{
                                    visible: visibility[field.name], // Use the specific field's visibility state
                                    onVisibleChange: () => handleVisibilityChange(field.name), // Toggle visibility for this field only
                                }}
                            />
                        )}
                    />
                );
            case 'radio':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={field?.defaultValue || field?.options?.[0]?.value}
                        render={(param) => (
                            field?.design == 'checkbox' ?
                                field?.options?.map((option) => (
                                    <Checkbox
                                        key={option.value}
                                        checked={selectedRadioValue === option.value}
                                        onChange={() => handleSelectedRadioValue(field?.name, option.value)}
                                    >
                                        {option.name}
                                    </Checkbox>
                                ))
                                :
                                <Radio.Group {...param.field} value={param.field.value} className={field?.classes} >
                                    {field?.options?.map((option) => (
                                        <Radio key={option.value} value={option.value} buttonStyle='solid'>
                                            {option.name}
                                        </Radio>
                                    ))}
                                </Radio.Group>
                        )}
                    />
                );
            case 'checkbox':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={false}
                        render={(param) => (
                            <Checkbox {...param.field} className={field?.classes}>{field?.label}</Checkbox>
                        )}
                    />
                );
            case 'switch':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={false}
                        render={(param) => <Switch {...param.field} />}
                    />
                );
            case 'datepicker':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={dayjs()}
                        render={(param) =>
                            <DatePicker
                                // allowClear={false} 
                                format={field?.format ? field?.format : 'DD-MM-YYYY'}
                                className={field?.classes}
                                placeholder={field.placeholder}
                                disabled={field?.disabled ?? false}
                                disabledDate={(current) => {
                                    return current && current.isBefore(dayjs().startOf('day'));
                                }}
                                {...param.field}
                            />
                        }
                    />

                );
            case 'timepicker':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={null}
                        render={(param) => (
                            <TimePicker
                                className={field?.classes}
                                placeholder={field.placeholder}
                                {...param.field}
                                showNow={false}
                                format="HH:mm"
                                disabledTime={(currentTime) => {
                                    const currentHour = current.hour();
                                    const currentMinute = current.minute();
                                    const currentSecond = current.second();

                                    return {
                                        disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter((hour) => {
                                            return hour < currentHour &&
                                                (allFields && dayjs(allFields?.date)?.format('YYYY-MM-DD') == dayjs().format('YYYY-MM-DD'))
                                        }
                                        ),
                                        disabledMinutes: (selectedHour) => {
                                            if (selectedHour === currentHour) {
                                                return Array.from({ length: 60 }, (_, i) => i).filter((minute) => {
                                                    return minute < currentMinute &&
                                                        (allFields && dayjs(allFields?.date)?.format('YYYY-MM-DD') == dayjs().format('YYYY-MM-DD'))
                                                }
                                                );
                                            }
                                            return [];
                                        },
                                        disabledSeconds: (selectedHour, selectedMinute) => {
                                            if (selectedHour === currentHour && selectedMinute === currentMinute) {
                                                return Array.from({ length: 60 }, (_, i) => i).filter((second) => {
                                                    return second < currentSecond &&
                                                        (allFields && dayjs(allFields?.date)?.format('YYYY-MM-DD') == dayjs().format('YYYY-MM-DD'))
                                                }
                                                );
                                            }
                                            return [];
                                        },
                                    };
                                }}

                            />
                        )
                        }
                    />
                );
            case 'textarea':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue=""
                        render={(param) => (
                            <Input.TextArea autoSize={field?.autoSize ? field?.autoSize : false} {...param.field} placeholder={field.placeholder} />
                        )}
                    />
                );
            case 'select':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue={field.multiple ? [] : ''}
                        render={(param) => (
                            <Select {...param.field} mode={field.multiple ? 'multiple' : 'default'} placeholder={field.placeholder} >
                                <Select.Option value=''>
                                    Select an option
                                </Select.Option>
                                {field?.options?.map((option) => (
                                    <Select.Option key={option.value} value={option.value}>
                                        {option.name}
                                    </Select.Option>
                                ))}
                            </Select>
                        )}
                    />
                );
            case 'file':
                return (
                    <Controller
                        key={field.name}
                        name={field.name}
                        control={control}
                        defaultValue=""
                        render={(param) => (
                            <Upload
                                {...param.field}
                                beforeUpload={() => {
                                    return false;
                                }}
                                onRemove={() => {
                                    return null;
                                }}
                                className='upload'
                            >
                                {
                                    field.component ? field.component : (
                                        <Button icon={<UploadOutlined />}>{field.label}</Button>
                                    )
                                }
                            </Upload>
                        )}
                    />
                );
            // case 'otp':
            //     return (
            //         <Controller
            //             name={field.name}
            //             control={control}
            //             defaultValue=""
            //             render={(param) => <Input.OTP onPaste={(e) => e.preventDefault()} length={field.length} {...param.field} />}
            //         />
            //     )
            case 'custom':
                return (
                    (field.component && field.name) ? (
                        <Controller
                            name={field.name}
                            control={control}
                            render={({ field: { onChange, onBlur, value, ref } }) =>
                                React.cloneElement(field.component, {
                                    onChange: (e) => {
                                        onChange(e);
                                        handleCustomComponentStateChange(field.name);
                                    },
                                    onBlur,
                                    value,
                                    ref,
                                    onEdit: () => {
                                        return props && props?.editData && props?.editData[field.name]
                                    }
                                })
                            }
                        />
                    )
                        : <></>
                )
            default:
                return null;
        }
    };

    useEffect(() => {
        if (props.onChange) props.onChange(allFields)
    }, [allFields])


    useEffect(() => {
        handleReset()
    }, [form.name, props?.reset === true])

    useEffect(() => {
        if (customComponentData && customComponentData.field) {
            setValue(customComponentData.field, customComponentData.value);
        }
    }, [customComponentData]);

    useEffect(() => {
        if (props?.editData) {
            for (const key in props?.editData) {
                setValue(key, props?.editData[key])
            }
            // reset(props?.editData);
        }
        else reset()
    }, [reset, props?.editData])

    return (
        <Form 
            id={props?.id} 
            layout="vertical" 
            onFinish={handleSubmit(formResponse)} 
            className='d-form w-full'
        >
            <Row gutter={16}>
                {form.formFields.map((field) => (
                    <Col key={field.name} md={field.width.md} sm={field.width.sm} xs={field.width.xs}>
                        <Form.Item
                            label={field.label.value}
                            
                            validateStatus={errors[field.name] ? 'error' : ''}
                            help={errors[field.name]?.message}
                            style={{ ...field.label.style }}
                            className={`${field.label.classes}`}
                        >
                            {renderFormField(field)}
                        </Form.Item>
                    </Col>
                ))}
            </Row>
            {/* <Form.Item>
                <Button type="primary" htmlType="submit">
                    Submit
                </Button>
            </Form.Item> */}
        </Form>
    );
};

export default DynamicForm;